package com.evoapps.evoshift.widgets

import androidx.compose.ui.unit.Constraints
import androidx.compose.ui.layout.Layout
import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.padding
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.Edit
import androidx.compose.material3.HorizontalDivider
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.runtime.Composable
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.vector.ImageVector
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.style.TextAlign
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.ui.DeepNavy
import com.evoapps.evoshift.ui.adaptive.handCursor
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.edit
import org.jetbrains.compose.resources.stringResource

/**
 * A settings / profile card: icon and title, an edit button when [onEdit] is set, and the
 * section's [DataRow]s. Used by the workplace settings and the employee form.
 */
@Composable
fun ProfileSection(
    title: String,
    icon: ImageVector,
    onEdit: (() -> Unit)?,
    /** A short explanation of the section behind a "?" next to the title ([HelpButton]). */
    help: String? = null,
    content: @Composable () -> Unit
) {
    RegularCard(
        modifier = Modifier.fillMaxWidth()
    ) {
        Column {
            Row(
                modifier = Modifier
                    .fillMaxWidth()
                    .padding(horizontal = 16.dp, vertical = 12.dp),
                horizontalArrangement = Arrangement.SpaceBetween,
                verticalAlignment = Alignment.CenterVertically
            ) {
                Row(verticalAlignment = Alignment.CenterVertically, modifier = Modifier.weight(1f)) {
                    Icon(
                        imageVector = icon,
                        contentDescription = null,
                        tint = DeepNavy,
                        modifier = Modifier.size(20.dp)
                    )
                    Spacer(modifier = Modifier.width(12.dp))
                    Text(
                        text = title,
                        style = MaterialTheme.typography.titleSmall,
                        fontWeight = FontWeight.Bold,
                        color = DeepNavy,
                        modifier = Modifier.weight(1f, fill = false)
                    )
                    if (help != null) {
                        Spacer(modifier = Modifier.width(4.dp))
                        HelpButton(title = title, text = help)
                    }
                }
                if (onEdit != null) {
                    IconButtonWithTooltip(
                        onClick = onEdit,
                        modifier = Modifier.size(32.dp).handCursor(),
                        tooltip = stringResource(Res.string.edit)
                    ) {
                        Icon(
                            Icons.Default.Edit,
                            contentDescription = stringResource(Res.string.edit),
                            tint = DeepNavy,
                            modifier = Modifier.size(18.dp)
                        )
                    }
                }
            }

            HorizontalDivider(
                modifier = Modifier.padding(horizontal = 16.dp),
                color = DeepNavy.copy(alpha = 0.1f)
            )

            Column(
                modifier = Modifier.padding(16.dp),
                verticalArrangement = Arrangement.spacedBy(8.dp)
            ) {
                content()
            }
        }
    }
}

/**
 * A label on the left and its value, bold and right-aligned, on the right. Side by side when both
 * fit; otherwise the width is shared by their lengths (each side at least [MIN_SHARE]) and both
 * wrap onto more lines, so a long label never squeezes the value into a column of single letters.
 */
@Composable
fun DataRow(label: String, value: String) {
    Layout(
        modifier = Modifier.fillMaxWidth(),
        content = {
            Text(
                text = label,
                style = MaterialTheme.typography.bodyMedium,
                color = DeepNavy.copy(alpha = 0.6f),
                fontWeight = FontWeight.Medium
            )
            Text(
                text = value,
                style = MaterialTheme.typography.bodyMedium,
                color = DeepNavy,
                fontWeight = FontWeight.Bold,
                textAlign = TextAlign.End
            )
        }
    ) { measurables, constraints ->
        val gap = DATA_ROW_GAP.roundToPx()
        val width = constraints.maxWidth
        val labelWidth = measurables[0].maxIntrinsicWidth(Constraints.Infinity)
        val valueWidth = measurables[1].maxIntrinsicWidth(Constraints.Infinity)
        val room = (width - gap).coerceAtLeast(0)
        val labelShare = if (labelWidth + valueWidth <= room) labelWidth
            else (room * labelWidth.toFloat() / (labelWidth + valueWidth).coerceAtLeast(1))
                .toInt().coerceIn((room * MIN_SHARE).toInt(), (room * (1 - MIN_SHARE)).toInt())
        val label = measurables[0].measure(Constraints(maxWidth = labelShare.coerceAtLeast(0)))
        val value = measurables[1].measure(Constraints(maxWidth = (room - label.width).coerceAtLeast(0)))
        layout(width, maxOf(label.height, value.height)) {
            label.placeRelative(0, 0)
            value.placeRelative(width - value.width, 0)
        }
    }
}

private val DATA_ROW_GAP = 16.dp
/** Neither side of a wrapped [DataRow] gets less than this share of the width. */
private const val MIN_SHARE = 0.4f
